import React, { useEffect, useState } from "react";
import { savedWisdom } from "../lib/api";
import { toast } from "sonner";
import { Bookmark, BookmarkCheck } from "lucide-react";
import PaywallModal from "./PaywallModal";

/**
 * Save/unsave toggle used on Devotional, Scripture, Wisdom-Path, and Guidance
 * cards. Same policy as Journal: writes gated for Free/Expired users, reads
 * always allowed. Dedupes on (kind, reference_id).
 *
 * Props:
 *  - kind: "devotional" | "scripture" | "wisdom_path" | "guidance"
 *  - referenceId: stable dedupe key (e.g., date, scripture ref, path id)
 *  - title, body: what to save
 *  - meta: optional dict
 *  - size: "sm" | "md"  (defaults to md)
 *  - label: override "Save" / "Saved" text; pass `false` for icon-only
 *  - testid: optional data-testid override
 */
export default function SaveWisdomButton({
  kind,
  referenceId,
  title,
  body,
  meta = null,
  size = "md",
  label,
  testid,
}) {
  const [saved, setSaved] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paywall, setPaywall] = useState(false);

  const canQuery = kind && referenceId;

  useEffect(() => {
    let cancel = false;
    if (!canQuery) return () => (cancel = true);
    (async () => {
      try {
        const res = await savedWisdom.exists(kind, referenceId);
        if (!cancel) {
          setSaved(!!res.saved);
          setSavedId(res.id || null);
        }
      } catch {
        /* silent — button just shows unsaved */
      }
    })();
    return () => {
      cancel = true;
    };
  }, [kind, referenceId, canQuery]);

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      if (saved && savedId) {
        await savedWisdom.remove(savedId);
        setSaved(false);
        setSavedId(null);
        toast.success("Removed from Saved Wisdom");
      } else {
        const created = await savedWisdom.create({
          kind,
          reference_id: referenceId,
          title,
          body,
          meta,
        });
        setSaved(true);
        setSavedId(created.id);
        toast.success("Saved to your Wisdom library");
      }
    } catch (err) {
      if (err?.response?.status === 402) {
        setPaywall(true);
      } else {
        toast.error(err?.response?.data?.detail || "Couldn't save this");
      }
    } finally {
      setBusy(false);
    }
  };

  const sizes = {
    sm: "text-[12px] px-2.5 py-1.5 gap-1.5",
    md: "text-sm px-4 py-2 gap-1.5",
  };
  const iconSize = size === "sm" ? 13 : 14;
  const Icon = saved ? BookmarkCheck : Bookmark;
  const text =
    label === false
      ? null
      : label ?? (saved ? "Saved" : "Save");

  return (
    <>
      <button
        onClick={toggle}
        disabled={busy}
        data-testid={testid || `save-wisdom-${kind}`}
        aria-pressed={saved}
        title={saved ? "Remove from Saved Wisdom" : "Save to Wisdom library"}
        className={`inline-flex items-center rounded-full font-medium border transition disabled:opacity-60 ${sizes[size]} ${
          saved
            ? "bg-[#4F6F52] hover:bg-[#3F5A42] text-white border-[#4F6F52] shadow-[0_2px_8px_rgba(79,111,82,0.18)]"
            : "bg-[#E1ECDD] hover:bg-[#D0DFCB] text-[#3F5A42] border-[#B7CCB1]"
        }`}
      >
        <Icon size={iconSize} strokeWidth={2.2} />
        {text}
      </button>
      <PaywallModal
        open={paywall}
        reason="Your LifeGuide journey is saved. Upgrade to LifeGuide Pro to save wisdom you want to keep."
        onClose={() => setPaywall(false)}
      />
    </>
  );
}
